// Záznam otevření produktu — jediné napojení sledování přístupů na UI (etapa K1).
// Viz doc/plans/usage-analytics-client.md sekce 2.3 a 2.4.
//
//   useTrackProduct(slug, { gh_id, subject_type, subject_id })
//
// Kontext a kategorii si hook dohledá v registru produktů podle slugu (projekce
// PRODUCT_BY_SLUG), takže volající předává jen to, co sám ví. Hook se volá v
// komponentě, která produkt vykresluje (StationDetailLayout, StationGroupLayout),
// ne v každém *View.jsx — tím se pokryjí i budoucí produkty bez dalšího zásahu.
//
// Systémové části (mapa, přihlášení, admin obrazovky, export) uzel v registru nemají
// a zapisují se přes useTrackView níže nebo přímým voláním track() z usageApi.

import { useEffect } from 'react'
import { track } from '../api/usageApi'
import { PRODUCT_BY_SLUG } from '../config/productRegistry'

// Opakované otevření téhož produktu do tohoto času se nezapočítá — aby přepínání
// tam a zpět nenafouklo čísla. Práh je schválně na jednom místě, ať se dá změnit.
const DEDUPE_WINDOW_MS = 30 * 1000

// slug (+ rozlišující část klíče) → čas posledního odeslání. Modulová, ne per-komponenta:
// deduplikace musí přežít odmountování produktu při přepnutí na jiný a zpět.
const lastSentAt = new Map()

// Deduplikace je per produkt × stanice × předmět — přepnutí na jinou stanici je nové
// zobrazení, i když je produkt stejný.
function dedupeKey(slug, gh_id, subject_type, subject_id) {
  return [slug, gh_id ?? '', subject_type ?? '', subject_id ?? ''].join('|')
}

function shouldSend(key) {
  const now = Date.now()
  const last = lastSentAt.get(key)
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false
  lastSentAt.set(key, now)
  return true
}

// Zaznamená otevření produktu z registru. Bez slugu (nebo když slug v registru není)
// se nic neposílá — server by takovou událost stejně zahodil podle whitelistu.
export function useTrackProduct(slug, { gh_id, subject_type, subject_id } = {}) {
  useEffect(() => {
    if (!slug) return
    const product = PRODUCT_BY_SLUG[slug]
    if (!product) return

    const key = dedupeKey(slug, gh_id, subject_type, subject_id)
    if (!shouldSend(key)) return

    track({
      context: product.context,
      category: product.category,
      product_slug: slug,
      gh_id,
      subject_type,
      subject_id,
    })
  }, [slug, gh_id, subject_type, subject_id])
}

// Zaznamená otevření systémové části, která uzel v registru nemá (mapa, přihlášení,
// admin obrazovky, export) — kontext se proto předává explicitně. Platí stejná
// deduplikace jako u produktů.
export function useTrackView(context, slug, { gh_id } = {}) {
  useEffect(() => {
    if (!context || !slug) return
    const key = dedupeKey(slug, gh_id)
    if (!shouldSend(key)) return
    track({ context, product_slug: slug, gh_id })
  }, [context, slug, gh_id])
}

// Jednorázový záznam systémové části mimo render (přihlášení, odhlášení, export) —
// stejná deduplikace, ale volá se z event handleru, ne z efektu.
export function trackView(context, slug, extra = {}) {
  if (!context || !slug) return
  const key = dedupeKey(slug, extra.gh_id, extra.subject_type, extra.subject_id)
  if (!shouldSend(key)) return
  track({ context, product_slug: slug, ...extra })
}
